/**
 * Responsibility: the cards you kept, as a shelf.
 *
 * Its own page rather than a tab on the collection. A liked card is not
 * something you own, and counting it in a collection's totals would make
 * those totals mean something else. It is the step before owning one.
 *
 * The rows are re-read from the catalogue rather than drawn from the
 * stored like. A like records what the card was worth on the day it was
 * kept, and that baseline is the whole value of the list - it is what a
 * later reading gets compared against - so showing it back as though it
 * were current would be the wrong number. That costs a catalogue page read
 * per card, which is why the page says it is working.
 *
 * Same shape as the collection and the watch list: figures, then one row of
 * tabs and controls, then the shelf.
 */

import { useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import { MetricCard, PageContainer, PageTabs } from '@kit/components';
import { ToolbarIconButton } from '@kit/components/buttons';
import { MarketTable } from '../components/market/MarketTable';
import { MarketCardDialog } from '../components/market/MarketCardDialog';
import { AddHoldingDialog } from '../components/collection/AddHoldingDialog';
import { GroupingGrid } from '../components/shelf/GroupingGrid';
import { ShelfGrid } from '../components/shelf/ShelfGrid';
import { ShelfToolbar } from '../components/shelf/ShelfToolbar';
import type { ShelfView } from '../components/shelf/ShelfToolbar';
import { fromMarketCard } from '../components/shelf/shelfCard';
import {
  useAddToCollection,
  useLikedCards,
  useSaved,
  useToggleWatch,
  useUnlikeFromBoard,
} from '../api/hooks/useMarket';
import { money } from '../components/grading/money';
import { FittedTable } from '../components/shelf/FittedTable';
import { useFillHeight } from '../hooks/useFillHeight';
import { usePageState } from '../hooks/usePageState';
import { useTags, useAddHoldingWithTags } from '../api/hooks/useTags';
import { TagManagerDialog } from '../components/collection/TagManagerDialog';
import { LikedTagPicker } from '../components/collection/LikedTagPicker';
import { tagGroupings } from '../components/collection/groupings';
import type { MarketResult } from '../api/client';

export function LikesPage() {
  const [view, setView] = usePageState<ShelfView>('likes.view', 'grid', 'durable');
  const [expanded, setExpanded] = usePageState('likes.expanded', false, 'durable');
  const [detail, setDetail] = usePageState<MarketResult | null>('likes.open', null);
  const [adding, setAdding] = usePageState<MarketResult | null>('likes.adding', null);

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Measured, not guessed: the room under the table changes when the
  // figures above it are hidden, and a constant allowance cannot know.
  const [fillRef, tableHeight] = useFillHeight();

  const [tagFilter, setTagFilter] = usePageState<string>('likes.tag', '');
  const [section, setSection] = usePageState<string>('likes.section', 'likes', 'durable');
  const [managingTags, setManagingTags] = usePageState('likes.tagManager', false);
  const tagList = useTags();

  const likes = useLikedCards(true);
  const saved = useSaved();
  const unlike = useUnlikeFromBoard();
  const add = useAddToCollection();
  const addWithTags = useAddHoldingWithTags();

  const tags = useMemo(() => tagList.data ?? [], [tagList.data]);
  const likedCards = useMemo(() => saved.data?.likes ?? [], [saved.data]);
  // Which liked cards are in the grouping being looked at. Ids come off
  // the saved list, which is where tags live; the priced rows below are a
  // different shape and carry none.
  const inTag = useMemo(
    () =>
      new Set(
        likedCards
          .filter((card) => (card.tags ?? []).includes(tagFilter))
          .map((card) => card.product_key),
      ),
    [likedCards, tagFilter],
  );
  const allRows = useMemo(() => likes.data?.results ?? [], [likes.data]);
  const rows = useMemo(
    () => (tagFilter ? allRows.filter((row) => inTag.has(row.key)) : allRows),
    [allRows, inTag, tagFilter],
  );
  // **This is the "what is left" view.** A sub-collection spans cards
  // owned and cards only found, so the liked side of one is exactly the
  // part still to buy.
  const sections = useMemo(
    () => [
      { key: 'likes', label: 'Liked' },
      { key: 'groups', label: 'Sub-collections' },
    ],
    [],
  );
  const tagGroups = useMemo(
    () => tagGroupings(tags, [], likedCards),
    [tags, likedCards],
  );
  const unresolved = likes.data?.unresolved ?? [];
  const cards = useMemo(() => rows.map(fromMarketCard), [rows]);
  const byKey = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);
  // Every row here is liked by definition, so the heart is always filled.
  const watchedKeys = useMemo(
    () => new Set((saved.data?.watching ?? []).map((row) => row.product_key)),
    [saved.data],
  );
  const toggleWatch = useToggleWatch(watchedKeys);
  const likedKeys = useMemo(
    () => new Set((saved.data?.likes ?? []).map((row) => row.product_key)),
    [saved.data],
  );

  const worth = rows.reduce((sum, row) => sum + Number(row.best_price ?? 0), 0);
  const moved = rows.filter((row) => Number(row.change ?? 0) !== 0).length;
  const error = likes.error ?? add.error ?? unlike.error;
  const busy = add.isPending || unlike.isPending;

  // **A filtered list is not an empty one, and saying so cost real
  // alarm.** The sub-collection filter is page state and outlives a
  // reload, so a filter left on showed a list of nothing under the words
  // "Nothing liked yet" - which reads as the likes having been deleted,
  // and was reported as exactly that. The message now says which of the
  // two it is, and the filter is named rather than merely hinted at.
  const filterName = tagFilter ? tags.find((tag) => tag.id === tagFilter)?.name : undefined;
  const empty = tagFilter
    ? `No liked cards in ${filterName ?? 'this sub-collection'} yet. Clear the filter above to see all ${allRows.length} liked ${allRows.length === 1 ? 'card' : 'cards'}.`
    : 'Nothing liked yet. The heart on any search or discovery row keeps a card here without adding it to your collection.';

  return (
    <PageContainer
      disableBodyScroll={onDesktop}
      // The card's own first row, so the strip sits flush at the top
      // rather than under a body's worth of padding.
      tabs={<PageTabs tabs={sections.map((entry) => ({ value: entry.key, label: entry.label }))} value={section} onChange={setSection} />}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}
        {add.isSuccess ? (
          <Alert severity="success" onClose={() => add.reset()}>
            Added to your collection.
          </Alert>
        ) : null}
        {unresolved.length > 0 ? (
          <Alert severity="warning">
            {unresolved.length} liked{' '}
            {unresolved.length === 1 ? 'card no longer has' : 'cards no longer have'} a catalogue
            page: {unresolved.map((card) => card.name).join(', ')}
          </Alert>
        ) : null}

        {!expanded ? (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              flexShrink: 0,
            }}
          >
            <MetricCard label="Liked" value={String(rows.length)} helper="kept for later" />
            <MetricCard
              label="Best grades"
              value={worth > 0 ? money(String(worth)) : '—'}
              helper="added up, at the dearest grade sold"
            />
            <MetricCard
              label="Moved today"
              value={String(moved)}
              helper="where the catalogue states a change"
            />
            <MetricCard
              label="Unresolved"
              value={String(unresolved.length)}
              helper="catalogue page has gone"
            />
          </Box>
        ) : null}

        <ShelfToolbar
          view={view}
          onView={setView}
          expanded={expanded}
          onExpanded={setExpanded}
        >
          {tagFilter ? (
            <Button size="small" variant="outlined" onClick={() => setTagFilter('')}>
              {`Showing ${filterName ?? 'one sub-collection'} only — clear`}
            </Button>
          ) : null}
          <Button size="small" variant="outlined" onClick={() => setManagingTags(true)}>
            Sub-collections
          </Button>
        </ShelfToolbar>

        {section === 'groups' ? (
          <GroupingGrid
            groupings={tagGroups}
            emptyMessage="No sub-collections yet. Make one, then put liked cards in it to track what is still to buy."
            onOpen={(tagId) => {
              setTagFilter(tagId);
              setSection('likes');
            }}
          />
        ) : view === 'grid' ? (
          <ShelfGrid
            cards={cards}
            onOpen={(card) => setDetail(byKey.get(card.key) ?? null)}
            emptyMessage={likes.isFetching ? 'Re-reading prices for your liked cards' : empty}
            corner={(card) => (
              <ToolbarIconButton
                aria-label="Stop liking this card"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  unlike.mutate(card.key);
                }}
                sx={{ bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
              >
                <HeartBrokenIcon fontSize="small" sx={{ color: 'common.white' }} />
              </ToolbarIconButton>
            )}
          />
        ) : (
          <FittedTable fillRef={fillRef}>
            <MarketTable
              rows={rows}
              loading={likes.isFetching}
              busy={busy}
              onOpen={setDetail}
              onAdd={setAdding}
              onLike={(row) => unlike.mutate(row.key)}
              liked={likedKeys}
              watched={watchedKeys}
              onWatch={(row) => toggleWatch.mutate(row.key)}
              maxHeight={onDesktop ? tableHeight : 0}
            />
          </FittedTable>
        )}
      </Stack>

      <TagManagerDialog
        open={managingTags}
        onClose={() => setManagingTags(false)}
        tags={tags}
      />

      <MarketCardDialog
        extra={
          detail
            ? (() => {
                const kept = likedCards.find((c) => c.product_key === detail.key);
                return kept ? <LikedTagPicker card={kept} tags={tags} /> : null;
              })()
            : null
        }
        card={detail}
        onClose={() => setDetail(null)}
        onAdd={(card) => {
          setDetail(null);
          setAdding(card);
        }}
      />

      <AddHoldingDialog
        card={adding}
        busy={add.isPending}
        onClose={() => setAdding(null)}
        onAdd={(body, tagIds) => {
          addWithTags.mutate({ body, tagIds });
          setAdding(null);
        }}
      />
    </PageContainer>
  );
}
